<template>
    <div class="ph-details">
        <div class="ph-details__background">
            <img src="~@/assets/images/dashboard-admin.png" alt="kasanowa" />
        </div>
        <KLayoutWrapper>
            <template #default>
                <div>
                    <div v-if="salesRepItems != null && salesRepItems.name != null && salesRepItems.surname != null" class="ph-details__admin-title">{{ salesRepItems.name }} {{ salesRepItems.surname }}</div>
                    <div>
                        <div class="ph-details__tab-wrapper">
                            <v-tabs v-model="tab" :bg-color="primary800" show-arrows center-active align-tabs="center">
                                <v-tab :id="SALES_REP_TAB_CHALLENGES" :value="SALES_REP_TAB_CHALLENGES">{{ $t('salesRep.challengesTab') }}</v-tab>
                                <v-tab :id="SALES_REP_TAB_QUIZ" :value="SALES_REP_TAB_QUIZ">{{ $t('salesRep.quizTab') }}</v-tab>
                                <v-tab :id="SALES_REP_TAB_USER" :value="SALES_REP_TAB_USER">{{ $t('salesRep.userInfo') }}</v-tab>
                            </v-tabs>
                        </div>

                        <v-window v-model="tab">
                            <v-window-item :value="SALES_REP_TAB_CHALLENGES">
                                <PHChallengesTable />
                            </v-window-item>
                            <v-window-item :value="SALES_REP_TAB_QUIZ">
                                <PHQuizTable />
                            </v-window-item>
                            <v-window-item :value="SALES_REP_TAB_USER">
                                <PHInfo :user-details="salesRepItems" />
                            </v-window-item>
                        </v-window>

                        <div class="ph-details__second-wrapper">
                            <div class="ph-details__second-item-wrapper">
                                <div class="ph-details__second-title">{{ $t('salesRep.logins') }}</div>
                                <PHLoginsTable />
                            </div>
                            <div class="ph-details__second-item-wrapper">
                                <div class="ph-details__second-title">{{ $t('salesRep.cardNumberTitle') }}</div>
                                <PHCardsTable :loading="loading" :user-details="salesRepItems" :handle-add-card-action="salesRepDetails" />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </KLayoutWrapper>
    </div>
</template>

<script setup lang="ts">
import { ref } from '@vue/runtime-core';
import router from '@/router';
import SalesRepApi from '@/api/v1/SalesRepApi';
import variables from '@/assets/scss/modules/variables.module.scss';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { navigateToWithErrorToast } from '@/hooks/NavigateHooks';
import { SALES_REP_TAB_CHALLENGES, SALES_REP_TAB_QUIZ, SALES_REP_TAB_USER } from '@/hooks/SalesRepHooks';
import type { ISalesRepDetailsResponse } from '@/models/SalesRepModel';
import KLayoutWrapper from '@/components/atoms/KLayoutWrapper.vue';
import PHChallengesTable from '@/views/PH/components/PHChallengesTable.vue';
import PHLoginsTable from '@/views/PH/components/PHLoginsTable.vue';
import PHCardsTable from '@/views/PH/components/PHCardsTable.vue';
import PHInfo from '@/views/PH/components/PHInfo.vue';
import PHQuizTable from '@/views/PH/components/PHQuizTable.vue';

const { isAdminWholesaler, userInfo } = storeToRefs(useUserStore());
const { primary800 } = variables;

const tab = ref<string>(SALES_REP_TAB_CHALLENGES);

const loading = ref<boolean>(false);
const salesRepItems = ref<ISalesRepDetailsResponse | null>(null);
const salesRepApi = new SalesRepApi();
const salesRepDetails = async() => {
    loading.value = true;
    try {
        const result = await salesRepApi.salesRepDetails(router.currentRoute.value.params.id);
        if (!result.isSuccess) {
            navigateToWithErrorToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('salesRep.toast.errorSalesRepDetailsPage'))
            loading.value = false;
            return;
        }
        salesRepItems.value = result.payload;
    } catch (e) {
        navigateToWithErrorToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('salesRep.toast.errorSalesRepDetailsPage'))
        loading.value = false;
        return;
    }
    loading.value = false;
};
salesRepDetails();
</script>

<style scoped lang="scss">
.ph-details {
    width: 100%;
    position: relative;

    &__background {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        width: 100%;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: calc(100% - 184px);
            min-height: calc(100vh - 184px);

            @include media-breakpoint-up(lg) {
                height: calc(100% - 217px);
                min-height: calc(100vh - 217px);
            }
        }
    }

    &__admin-title {
        background-color: $primary-400;
        color: $black;
        font-weight: 1000;
        padding: 8px;
        text-transform: uppercase;
        font-size: 20px;
        text-align: center;
        max-width: 520px;
        margin: 0 auto 20px;

        @include media-breakpoint-up(lg) {
            font-size: 40px;
            padding: 18px 24px;
            max-width: 900px;
        }
    }

    &__name-wrapper {
        display: flex;
        color: $white;
        text-transform: uppercase;
        font-size: 18px;
        justify-content: center;
        margin: 30px 0 56px;
    }

    &__tab-wrapper {
        margin: 0 auto;

        @include media-breakpoint-up(sm) {
            width: max-content;
        }

        &:deep(.v-btn__content) {
            letter-spacing: 0;
            text-transform: uppercase;
        }
        &:deep(.v-btn--size-default) {
            padding: 0 35px;
        }
        &:deep(.v-slide-group__content) {
            justify-content: center;
        }
        &:deep(.v-tab--selected .v-tab__slider) {
            opacity: 1;
            background-color: $secondary-400;
        }
        &:deep(.v-btn) {
            background-color: rgba($black, 0.65);
        }
        &:deep(.v-tab--selected) {
            opacity: 1;
            background-color: $primary-800;
        }
        &:deep(.v-slide-group__next) {
            background-color: $primary-400;
        }
        &:deep(.v-slide-group__prev) {
            background-color: $primary-400;
        }
    }

    &__second-wrapper {
        margin-top: 51px;
        width: 100%;
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
        }
    }

    &__second-item-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            width: 50%;
        }
    }

    &__second-title {
        padding: 15px 10px;
        background-color: $secondary-400;
        font-weight: 700;
        text-transform: uppercase;
    }
}
</style>
